// Order Tracking Page - White-label customer-facing tracking
import { useState, useEffect } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import { Package, Truck, CheckCircle2, Clock, MapPin, Search, ChevronRight, Home, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Status icon mapping
const STATUS_ICONS = {
  'Order Placed': Clock,
  'Order Confirmed': CheckCircle2,
  'Payment Confirmed': CheckCircle2,
  'Processing': Package,
  'Order Processing': Package,
  'Preparing Shipment': Package,
  'Shipped': Truck,
  'In Transit': Truck,
  'Out for Delivery': Truck,
  'Delivered': CheckCircle2,
  'Cancelled': AlertCircle,
  'Refunded': AlertCircle
};

// Status color mapping
const STATUS_COLORS = {
  'Order Placed': 'bg-blue-500',
  'Order Confirmed': 'bg-blue-500',
  'Payment Confirmed': 'bg-emerald-500',
  'Processing': 'bg-amber-500',
  'Order Processing': 'bg-amber-500',
  'Preparing Shipment': 'bg-amber-500',
  'Shipped': 'bg-purple-500',
  'In Transit': 'bg-purple-500',
  'Out for Delivery': 'bg-emerald-500',
  'Delivered': 'bg-emerald-500',
  'Cancelled': 'bg-red-500',
  'Refunded': 'bg-gray-500'
};

const TrackingPage = () => {
  const { orderId } = useParams();
  const [searchParams] = useSearchParams();
  
  const [trackingData, setTrackingData] = useState(null);
  const [courierTracking, setCourierTracking] = useState(null);
  const [orderList, setOrderList] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchOrderId, setSearchOrderId] = useState(orderId || '');
  const [searchType, setSearchType] = useState('order'); // order, tracking, email
  
  // Fetch tracking data with courier details
  const fetchTracking = async (id) => {
    if (!id) return;
    
    setLoading(true);
    setError('');
    setOrderList(null);
    
    try {
      // Fetch courier-based tracking for live updates
      const courierResponse = await axios.get(`${API_URL}/api/track/courier/${id}`);
      setCourierTracking(courierResponse.data);
      
      // Also fetch basic tracking for order details
      const email = searchParams.get('email');
      const url = email 
        ? `${API_URL}/api/track/${id}?email=${encodeURIComponent(email)}`
        : `${API_URL}/api/track/${id}`;
      
      const response = await axios.get(url);
      setTrackingData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Order not found. Please check your order ID.');
      setTrackingData(null);
      setCourierTracking(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch by tracking number
  const fetchByTrackingNumber = async (trackingNum) => {
    if (!trackingNum) return;
    
    setLoading(true);
    setError('');
    setOrderList(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/track/number/${trackingNum}`);
      setCourierTracking(response.data);
      setTrackingData(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Tracking number not found.');
      setTrackingData(null);
      setCourierTracking(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch orders by email
  const fetchByEmail = async (email) => {
    if (!email) return;
    
    setLoading(true);
    setError('');
    setTrackingData(null);
    setCourierTracking(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/track/my-orders`, {
        params: { email: email.trim() }
      });
      setOrderList(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'No orders found for this email.');
      setOrderList(null);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch on mount if orderId provided
  useEffect(() => {
    if (orderId) {
      fetchTracking(orderId);
    }
  }, [orderId]);
  
  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    const value = searchOrderId.trim();
    if (!value) return;

    if (searchType === 'email') {
      fetchByEmail(value);
    } else if (searchType === 'tracking') {
      fetchByTrackingNumber(value);
    } else {
      fetchTracking(value);
    }
  };
  
  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-slate-900 text-white py-8">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center gap-2 text-slate-400 text-sm mb-4">
            <Link to="/" className="hover:text-white transition-colors">
              <Home className="w-4 h-4" />
            </Link>
            <ChevronRight className="w-4 h-4" />
            <span>Order Tracking</span>
          </div>
          
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
              <Package className="w-6 h-6 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">Track Your Order</h1>
              <p className="text-slate-400 text-sm">Enter your order ID to see delivery status</p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Search Form */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            {/* Search Type Tabs */}
            <div className="flex gap-2 mb-4">
              <Button
                type="button"
                variant={searchType === 'order' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('order')}
                className="flex-1"
              >
                <Package className="w-4 h-4 mr-1" />
                Order ID
              </Button>
              <Button
                type="button"
                variant={searchType === 'tracking' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('tracking')}
                className="flex-1"
              >
                <Truck className="w-4 h-4 mr-1" />
                Tracking #
              </Button>
              <Button
                type="button"
                variant={searchType === 'email' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSearchType('email')}
                className="flex-1"
              >
                <MapPin className="w-4 h-4 mr-1" />
                Email
              </Button>
            </div>

            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  placeholder={
                    searchType === 'email' 
                      ? 'your@email.com' 
                      : searchType === 'tracking' 
                      ? 'e.g., GD20240115ABC123' 
                      : 'Enter your order ID...'
                  }
                  value={searchOrderId}
                  onChange={(e) => setSearchOrderId(e.target.value)}
                  className="pl-10"
                  data-testid="tracking-search-input"
                />
              </div>
              <Button type="submit" disabled={loading} data-testid="tracking-search-button">
                {loading ? 'Searching...' : 'Track'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Order List (for email search) */}
        {orderList && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="text-lg">Your Orders ({orderList.count})</CardTitle>
              <CardDescription>Orders found for {orderList.email}</CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
              {orderList.orders.map((order) => (
                <div
                  key={order.order_id}
                  className="flex items-center justify-between p-4 rounded-lg border hover:bg-slate-50 cursor-pointer transition-colors"
                  onClick={() => {
                    setSearchType('order');
                    setSearchOrderId(order.order_id);
                    fetchTracking(order.order_id);
                  }}
                >
                  <div>
                    <p className="font-medium">Order #{order.order_id.slice(0, 8).toUpperCase()}</p>
                    <p className="text-sm text-gray-500">
                      {new Date(order.created_at).toLocaleDateString()} · {order.item_count} item(s)
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge className={`${STATUS_COLORS[order.status_text] || 'bg-gray-500'} text-white`}>
                      {order.status_text || order.fulfillment_status}
                    </Badge>
                    {order.tracking_number && (
                      <p className="text-xs text-gray-500 mt-1">{order.tracking_number}</p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
        
        {/* Error State */}
        {error && (
          <Card className="mb-8 border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3 text-red-600">
                <AlertCircle className="w-5 h-5" />
                <p>{error}</p>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Tracking Data */}
        {trackingData && (
          <>
            {/* Status Card */}
            <Card className="mb-6">
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <CardDescription>Order #{trackingData.order_id?.slice(0, 8).toUpperCase()}</CardDescription>
                    <CardTitle className="text-2xl mt-1">{trackingData.status}</CardTitle>
                  </div>
                  <Badge className={`${STATUS_COLORS[trackingData.status] || 'bg-gray-500'} text-white px-4 py-1.5 text-sm`}>
                    {trackingData.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 mb-4">{trackingData.status_description}</p>
                
                {trackingData.estimated_delivery && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-emerald-700">
                      <Truck className="w-5 h-5" />
                      <span className="font-medium">Estimated Delivery:</span>
                      <span>{trackingData.estimated_delivery}</span>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Courier Tracking Info */}
            {courierTracking && courierTracking.tracking_number && (
              <Card className="mb-6 border-purple-200 bg-purple-50/30">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Truck className="w-5 h-5 text-purple-600" />
                    Live Courier Tracking
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-gray-500">Tracking Number</p>
                      <p className="font-mono font-medium">{courierTracking.tracking_number}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Courier</p>
                      <p className="font-medium">{courierTracking.courier_name || courierTracking.courier}</p>
                    </div>
                    {courierTracking.shipped_at && (
                      <div>
                        <p className="text-gray-500">Shipped</p>
                        <p className="font-medium">{new Date(courierTracking.shipped_at).toLocaleDateString()}</p>
                      </div>
                    )}
                    {courierTracking.estimated_delivery && (
                      <div>
                        <p className="text-gray-500">Est. Delivery</p>
                        <p className="font-medium text-emerald-600">{courierTracking.estimated_delivery}</p>
                      </div>
                    )}
                  </div>
                  
                  {courierTracking.current_location && (
                    <div className="mt-4 flex items-center gap-2 text-sm bg-white p-3 rounded-lg border">
                      <MapPin className="w-4 h-4 text-purple-600" />
                      <span>Current Location: <strong>{courierTracking.current_location}</strong></span>
                    </div>
                  )}

                  {/* Live Tracking Events */}
                  {courierTracking.events && courierTracking.events.length > 0 && (
                    <div className="mt-4 pt-4 border-t">
                      <p className="text-sm font-medium mb-3">Live Updates from {courierTracking.courier_name || courierTracking.courier}</p>
                      <div className="space-y-3">
                        {courierTracking.events.slice(0, 5).map((event, idx) => (
                          <div key={idx} className="flex gap-3 text-sm">
                            <div className={`w-2 h-2 rounded-full mt-1.5 ${idx === 0 ? 'bg-purple-500' : 'bg-gray-300'}`} />
                            <div className="flex-1">
                              <p className="font-medium">{event.description || event.status}</p>
                              <p className="text-gray-500 text-xs">
                                {event.location && `${event.location} · `}
                                {new Date(event.timestamp).toLocaleString()}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}
            
            {/* Tracking Timeline */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Tracking History</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trackingData.tracking_events?.map((event, index) => {
                    const Icon = STATUS_ICONS[event.status] || Package;
                    const isLatest = index === 0;
                    
                    return (
                      <div 
                        key={index}
                        className={`flex gap-4 ${isLatest ? 'opacity-100' : 'opacity-60'}`}
                      >
                        {/* Timeline Line */}
                        <div className="flex flex-col items-center">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            isLatest 
                              ? `${STATUS_COLORS[event.status] || 'bg-gray-500'} text-white` 
                              : 'bg-gray-100 text-gray-400'
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          {index < trackingData.tracking_events.length - 1 && (
                            <div className="w-0.5 h-full bg-gray-200 mt-2" />
                          )}
                        </div>
                        
                        {/* Event Details */}
                        <div className="flex-1 pb-6">
                          <div className="flex items-start justify-between">
                            <div>
                              <h4 className={`font-medium ${isLatest ? 'text-gray-900' : 'text-gray-600'}`}>
                                {event.status}
                              </h4>
                              <p className="text-gray-500 text-sm mt-0.5">{event.description}</p>
                            </div>
                            <span className="text-xs text-gray-400">
                              {new Date(event.timestamp).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </span>
                          </div>
                          {event.location && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-gray-400">
                              <MapPin className="w-3 h-3" />
                              {event.location}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
            
            {/* Order Items */}
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="text-lg">Order Items</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trackingData.items?.map((item, index) => (
                    <div key={index} className="flex items-center gap-4">
                      {item.image_url && (
                        <img 
                          src={item.image_url} 
                          alt={item.name}
                          className="w-16 h-16 rounded-lg object-cover bg-gray-100"
                        />
                      )}
                      <div className="flex-1">
                        <h4 className="font-medium text-gray-900">{item.name}</h4>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <span className="font-medium">${item.price?.toFixed(2)}</span>
                    </div>
                  ))}
                  
                  <Separator className="my-4" />
                  
                  <div className="flex justify-between items-center">
                    <span className="font-medium text-gray-700">Total</span>
                    <span className="text-xl font-bold text-gray-900">
                      ${trackingData.total?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Shipping Address */}
            {trackingData.shipping_address && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Shipping Address</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                    <p className="text-gray-600">{trackingData.shipping_address}</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}
        
        {/* Empty State */}
        {!trackingData && !loading && !error && (
          <Card className="py-12">
            <CardContent className="text-center">
              <Package className="w-16 h-16 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                Track Your Package
              </h3>
              <p className="text-gray-500 max-w-md mx-auto">
                Enter your order ID above to see the current status and delivery timeline of your order.
              </p>
            </CardContent>
          </Card>
        )}
        
        {/* Help Section */}
        <div className="mt-8 text-center">
          <p className="text-gray-500 text-sm">
            Need help? <a href="mailto:support@salesspace-in.com" className="text-emerald-600 hover:underline">Contact Support</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default TrackingPage;
